// react
import { useContext, useMemo, useState } from 'react';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// mui
import {
	alpha,
	Avatar,
	Box,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Tab,
	Tabs,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	ArrowForward as IconArrow,
	Assignment as IconWorkPlan,
	CheckCircle as IconCheckCircle,
	Description as IconContract,
	ErrorOutline as IconAlert,
	ExpandLess as IconExpandLess,
	ExpandMore as IconExpandMore,
	GppGood as IconCondition,
	HourglassEmpty as IconPending,
	Info as IconInfo,
	OpenInNew as IconOpen,
	Payments as IconPayments,
	Receipt as IconInvoice,
	Schedule as IconSchedule,
	Speed as IconDashboard,
	TrendingUp as IconTrending,
	Warning as IconWarning,
} from '@mui/icons-material';

// recharts
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';

// components
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { resolveContractFxRate } from '../../../../utils/fxRate';

// Types

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

interface ActionItem {
	id: string;
	type: 'invoice' | 'condition' | 'payment' | 'contract' | 'milestone';
	title: string;
	subtitle: string;
	severity: 'info' | 'warning' | 'urgent';
	link: string;
	date?: string;
	amount?: number;
	currency?: string;
}

interface ContractSummaryRow {
	contractId: number;
	contractNum: string;
	activityName: string;
	totalAmount: number;
	totalPaid: number;
	currency: string;
	totalAmountLocal: string | null;
	totalPaidLocal: string | null;
	conditionsTotal: number;
	conditionsVerified: number;
	invoicesPending: number;
	vouchersReady: number;
	status: string;
	upcomingMilestones: number;
}

// Tab Panel Component

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`ops-dashboard-tabpanel-${index}`}
			aria-labelledby={`ops-dashboard-tab-${index}`}
			{...other}>
			{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
		</div>
	);
};

// Summary Card Component (reusable)

interface SummaryCardProps {
	label: string;
	value: string | number;
	icon: React.ReactElement;
	color: string;
	subtitle?: string;
	onClick?: () => void;
	progress?: { value: number; label: string };
}

const SummaryCard = ({
	label,
	value,
	icon,
	color,
	subtitle,
	onClick,
	progress,
}: SummaryCardProps) => {
	const content = (
		<CardContent sx={{ p: 2.5 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Box>
					<Typography variant="caption" color="text.secondary" fontWeight={500}>
						{label}
					</Typography>
					<Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
						{value}
					</Typography>
				</Box>
				<Avatar
					sx={{
						width: 48,
						height: 48,
						bgcolor: alpha(color, 0.1),
					}}>
					{icon}
				</Avatar>
			</Stack>
			{subtitle && (
				<Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
					{subtitle}
				</Typography>
			)}
			{progress && (
				<Box sx={{ mt: 1.5 }}>
					<LinearProgress
						variant="determinate"
						value={progress.value}
						sx={{
							height: 6,
							borderRadius: 3,
							bgcolor: alpha(color, 0.1),
							'& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: color },
						}}
					/>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
						{progress.label}
					</Typography>
				</Box>
			)}
		</CardContent>
	);

	return (
		<Card
			sx={{
				height: '100%',
				borderRadius: 3,
				border: `1px solid ${alpha(color, 0.15)}`,
				transition: 'all 0.2s ease',
				...(onClick && {
					'&:hover': {
						borderColor: color,
						boxShadow: `0 4px 12px ${alpha(color, 0.15)}`,
					},
				}),
			}}>
			{onClick ? <CardActionArea onClick={onClick}>{content}</CardActionArea> : content}
		</Card>
	);
};

// Action Item Card Component

const ActionItemCard = ({
	item,
	onNavigate,
}: {
	item: ActionItem;
	onNavigate: (link: string) => void;
}) => {
	const theme = useTheme();

	const severityColors = {
		info: theme.palette.info.main,
		warning: theme.palette.warning.main,
		urgent: theme.palette.error.main,
	};

	const severityIcons = {
		info: <IconInfo sx={{ fontSize: 18 }} />,
		warning: <IconWarning sx={{ fontSize: 18 }} />,
		urgent: <IconAlert sx={{ fontSize: 18 }} />,
	};

	const typeIcons = {
		invoice: <IconInvoice sx={{ fontSize: 20 }} />,
		condition: <IconCondition sx={{ fontSize: 20 }} />,
		payment: <IconPayments sx={{ fontSize: 20 }} />,
		contract: <IconContract sx={{ fontSize: 20 }} />,
		milestone: <IconSchedule sx={{ fontSize: 20 }} />,
	};

	const color = severityColors[item.severity];

	return (
		<Paper
			sx={{
				p: 2,
				borderRadius: 2,
				border: `1px solid ${alpha(color, 0.2)}`,
				borderLeft: `4px solid ${color}`,
				transition: 'all 0.2s ease',
				cursor: 'pointer',
				'&:hover': {
					borderColor: color,
					boxShadow: `0 4px 12px ${alpha(color, 0.1)}`,
				},
			}}
			onClick={() => onNavigate(item.link)}>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Avatar sx={{ width: 36, height: 36, bgcolor: alpha(color, 0.1) }}>
						{typeIcons[item.type]}
					</Avatar>
					<Box>
						<Stack direction="row" alignItems="center" spacing={1}>
							<Typography variant="subtitle2" fontWeight={600}>
								{item.title}
							</Typography>
							<Chip
								size="small"
								icon={severityIcons[item.severity]}
								label={item.severity}
								sx={{
									height: 20,
									fontSize: '0.65rem',
									bgcolor: alpha(color, 0.1),
									color: color,
									fontWeight: 600,
									textTransform: 'capitalize',
									'& .MuiChip-icon': { color: color, fontSize: 14 },
								}}
							/>
						</Stack>
						<Typography variant="caption" color="text.secondary">
							{item.subtitle}
						</Typography>
					</Box>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={1}>
					{item.amount != null && (
						<Typography variant="subtitle2" fontWeight={700}>
							{formatCurrency({ value: item.amount, currency: item.currency || 'USD' })}
						</Typography>
					)}
					{item.date && (
						<Typography variant="caption" color="text.secondary">
							{item.date}
						</Typography>
					)}
					<IconButton size="small">
						<IconOpen sx={{ fontSize: 16 }} />
					</IconButton>
				</Stack>
			</Stack>
		</Paper>
	);
};

// Contract Row Component (expandable)

const ContractRow = ({ row, projectId }: { row: ContractSummaryRow; projectId: string }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const [expanded, setExpanded] = useState(false);
	const paymentProgress = row.totalAmount > 0 ? (row.totalPaid / row.totalAmount) * 100 : 0;

	const hasIssues = row.invoicesPending > 0 || row.vouchersReady > 0;

	return (
		<Paper
			sx={{
				borderRadius: 2,
				border: `1px solid ${theme.palette.divider}`,
				overflow: 'hidden',
				transition: 'all 0.2s ease',
				'&:hover': {
					borderColor: theme.palette.primary.main,
					boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
				},
			}}>
			{/* Main row */}
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{ p: 2, cursor: 'pointer' }}
				onClick={() => setExpanded(!expanded)}>
				<Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
					<Avatar
						sx={{
							width: 40,
							height: 40,
							bgcolor: alpha(theme.palette.primary.main, 0.08),
						}}>
						<IconContract sx={{ fontSize: 20, color: theme.palette.primary.main }} />
					</Avatar>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Stack direction="row" alignItems="center" spacing={1}>
							<Typography variant="subtitle2" fontWeight={600} noWrap>
								{row.contractNum}
							</Typography>
							<Chip
								label={row.status.replace(/_/g, ' ')}
								size="small"
								sx={{
									height: 20,
									fontSize: '0.65rem',
									fontWeight: 600,
									bgcolor: alpha(
										row.status === 'ACTIVE'
											? theme.palette.success.main
											: row.status === 'PENDING'
												? theme.palette.warning.main
												: theme.palette.grey[500],
										0.1
									),
									color:
										row.status === 'ACTIVE'
											? theme.palette.success.main
											: row.status === 'PENDING'
												? theme.palette.warning.main
												: theme.palette.grey[500],
								}}
							/>
							{hasIssues && (
								<Tooltip
									title={`${row.invoicesPending} pending invoices, ${row.vouchersReady} payments ready`}>
									<IconAlert sx={{ fontSize: 16, color: theme.palette.warning.main }} />
								</Tooltip>
							)}
						</Stack>
						<Typography variant="caption" color="text.secondary" noWrap>
							{row.activityName}
						</Typography>
					</Box>
				</Stack>

				{/* Financial summary */}
				<Stack direction="row" alignItems="center" spacing={3} sx={{ mr: 2 }}>
					<Box sx={{ textAlign: 'right', minWidth: 100 }}>
						<Typography variant="caption" color="text.secondary">
							Contract Value
						</Typography>
						<Typography variant="subtitle2" fontWeight={700}>
							{formatCurrency({ value: row.totalAmount, currency: row.currency })}
						</Typography>
						{row.totalAmountLocal && (
							<Typography variant="caption" sx={{ display: 'block', color: '#6b7280' }}>
								{row.totalAmountLocal}
							</Typography>
						)}
					</Box>
					<Box sx={{ textAlign: 'right', minWidth: 100 }}>
						<Typography variant="caption" color="text.secondary">
							Paid
						</Typography>
						<Typography variant="subtitle2" fontWeight={700} color="success.main">
							{formatCurrency({ value: row.totalPaid, currency: row.currency })}
						</Typography>
						{row.totalPaidLocal && (
							<Typography variant="caption" sx={{ display: 'block', color: '#9ca3af' }}>
								{row.totalPaidLocal}
							</Typography>
						)}
					</Box>
					<Box sx={{ width: 120 }}>
						<LinearProgress
							variant="determinate"
							value={Math.min(paymentProgress, 100)}
							sx={{
								height: 6,
								borderRadius: 3,
								bgcolor: alpha(theme.palette.success.main, 0.1),
								'& .MuiLinearProgress-bar': {
									borderRadius: 3,
									bgcolor: theme.palette.success.main,
								},
							}}
						/>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ mt: 0.25, display: 'block' }}>
							{paymentProgress.toFixed(0)}% paid
						</Typography>
					</Box>
				</Stack>

				<IconButton size="small">{expanded ? <IconExpandLess /> : <IconExpandMore />}</IconButton>
			</Stack>

			{/* Expanded details */}
			<Collapse in={expanded}>
				<Divider />
				<Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
					<Grid container spacing={2}>
						{/* Conditions progress */}
						<Grid item xs={12} sm={4}>
							<Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
								<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
									<IconCondition sx={{ fontSize: 18, color: theme.palette.info.main }} />
									<Typography variant="subtitle2" fontWeight={600}>
										Payment Conditions
									</Typography>
								</Stack>
								<Stack direction="row" alignItems="baseline" spacing={0.5}>
									<Typography variant="h5" fontWeight={700} color="info.main">
										{row.conditionsVerified}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										/ {row.conditionsTotal} verified
									</Typography>
								</Stack>
								{row.conditionsTotal > 0 && (
									<LinearProgress
										variant="determinate"
										value={(row.conditionsVerified / row.conditionsTotal) * 100}
										sx={{
											mt: 1,
											height: 4,
											borderRadius: 2,
											bgcolor: alpha(theme.palette.info.main, 0.1),
											'& .MuiLinearProgress-bar': {
												borderRadius: 2,
												bgcolor: theme.palette.info.main,
											},
										}}
									/>
								)}
							</Paper>
						</Grid>

						{/* Pending invoices */}
						<Grid item xs={12} sm={4}>
							<Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
								<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
									<IconInvoice sx={{ fontSize: 18, color: theme.palette.warning.main }} />
									<Typography variant="subtitle2" fontWeight={600}>
										Invoices Pending
									</Typography>
								</Stack>
								<Typography variant="h5" fontWeight={700} color="warning.main">
									{row.invoicesPending}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Awaiting review or approval
								</Typography>
							</Paper>
						</Grid>

						{/* Payments ready */}
						<Grid item xs={12} sm={4}>
							<Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
								<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
									<IconPayments sx={{ fontSize: 18, color: theme.palette.success.main }} />
									<Typography variant="subtitle2" fontWeight={600}>
										Payments Ready
									</Typography>
								</Stack>
								<Typography variant="h5" fontWeight={700} color="success.main">
									{row.vouchersReady}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Verified and ready for disbursement
								</Typography>
							</Paper>
						</Grid>
					</Grid>

					{/* Quick actions for this contract */}
					<Stack direction="row" spacing={1} sx={{ mt: 2 }}>
						<Chip
							label="View Contract"
							size="small"
							icon={<IconArrow sx={{ fontSize: 14 }} />}
							onClick={() =>
								navigate(`/project/${projectId}/spend-plans/contract/${row.contractId}`)
							}
							sx={{
								fontWeight: 500,
								'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
							}}
						/>
						{row.invoicesPending > 0 && (
							<Chip
								label="Review Invoices"
								size="small"
								icon={<IconInvoice sx={{ fontSize: 14 }} />}
								onClick={() => navigate(`/project/${projectId}/spend-plans/invoices`)}
								sx={{
									fontWeight: 500,
									color: theme.palette.warning.main,
									borderColor: alpha(theme.palette.warning.main, 0.3),
									'&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.1) },
								}}
								variant="outlined"
							/>
						)}
						{row.vouchersReady > 0 && (
							<Chip
								label="Process Payments"
								size="small"
								icon={<IconPayments sx={{ fontSize: 14 }} />}
								onClick={() => navigate(`/project/${projectId}/spend-plans/vouchers`)}
								sx={{
									fontWeight: 500,
									color: theme.palette.success.main,
									borderColor: alpha(theme.palette.success.main, 0.3),
									'&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) },
								}}
								variant="outlined"
							/>
						)}
					</Stack>
				</Box>
			</Collapse>
		</Paper>
	);
};

// Project Operations Dashboard Component

export const ProjectOperationsDashboard = () => {
	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();
	const theme = useTheme();

	// context
	const { project } = useContext(ContextProject);
	const {
		activities,
		contracts,
		conditions,
		vouchers,
		workPlans,
		exceptions,
		summary,
		getConditionsByContract,
		getContractsByActivity,
		getVouchersByContract,
	} = useContext(ContextSpendPlan);

	// state
	const [tabValue, setTabValue] = useState(0);

	const projectId = project?.idWbg || '';
	const defaultCurrency = summary.currency || activities[0]?.currency || 'USD';

	// =========================================================================
	// Computed data
	// =========================================================================

	// Enriched contract summaries
	const contractSummaries = useMemo<ContractSummaryRow[]>(() => {
		return contracts.map((contract) => {
			const activity = activities.find((a) => a.id === contract.planningActivity);

			const contractConditions = getConditionsByContract(contract.id);
			const contractVouchers = getVouchersByContract(contract.id);

			const totalAmount =
				contract.amounts?.reduce(
					(sum: number, a: { amount?: number }) => sum + (a.amount || 0),
					0
				) || 0;
			const totalPaid =
				(contract.totalPaidReporting ||
					contractVouchers
						.filter((v) => v.status === 'VERIFIED' || v.status === 'REDEEMED')
						.reduce((sum, v) => sum + (v.amount || 0), 0)) + (contract.totalPaidV1 || 0);

			// Restate into the project's local currency via the canonical resolver,
			// which prefers the contract's own basis (commitment scalars first, then
			// its multi-currency amount rows) and only falls back to the work plan
			// when the contract has nothing to offer.
			const workPlan = workPlans.find((wp) => wp.id === contract.workPlan);
			const fx = resolveContractFxRate(contract, workPlan, project);
			const rowCurrency = activity?.currency || defaultCurrency;
			const canLocalize = fx != null && rowCurrency !== fx.localCurrency;

			return {
				contractId: contract.id,
				contractNum: contract.num || `C-${contract.id}`,
				activityName: activity?.name || 'Unlinked Activity',
				totalAmount,
				totalPaid,
				currency: rowCurrency,
				totalAmountLocal:
					canLocalize && fx
						? formatCurrency({ currency: fx.localCurrency, value: totalAmount * fx.rate })
						: null,
				totalPaidLocal:
					canLocalize && fx
						? formatCurrency({ currency: fx.localCurrency, value: totalPaid * fx.rate })
						: null,
				conditionsTotal: contractConditions.length,
				conditionsVerified: contractConditions.filter((c) => c.status === 'VERIFIED').length,
				invoicesPending: 0, // Invoices don't come from context currently; this is a placeholder
				vouchersReady: contractVouchers.filter((v) => v.status === 'READY').length,
				status: contract.status || 'ACTIVE',
				upcomingMilestones: contractConditions.filter(
					(c) => c.status === 'PENDING' && c.milestoneType != null
				).length,
			};
		});
	}, [
		contracts,
		activities,
		workPlans,
		project,
		defaultCurrency,
		getConditionsByContract,
		getVouchersByContract,
	]);

	// Pending action items
	const actionItems = useMemo<ActionItem[]>(() => {
		const items: ActionItem[] = [];

		// Pending conditions that need verification
		const pendingConditions = conditions.filter((c) => c.status === 'PENDING');
		pendingConditions.slice(0, 5).forEach((condition) => {
			items.push({
				id: `cond-${condition.id}`,
				type: 'condition',
				title: condition.name || `Condition #${condition.id}`,
				subtitle: 'Payment condition awaiting verification',
				severity: 'warning',
				link: `/project/${projectId}/spend-plans/payment-condition/${condition.id}`,
				date: condition.created
					? new Date(condition.created).toLocaleDateString('en-US', {
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})
					: undefined,
			});
		});

		// Vouchers ready for processing
		const readyVouchers = vouchers.filter((v) => v.status === 'READY');
		readyVouchers.slice(0, 5).forEach((voucher) => {
			items.push({
				id: `voucher-${voucher.id}`,
				type: 'payment',
				title: `Payment #${voucher.id}`,
				subtitle: 'Verified and ready for disbursement',
				severity: 'info',
				link: `/project/${projectId}/spend-plans/voucher/${voucher.id}`,
				amount: voucher.amount,
				currency: voucher.currency || defaultCurrency,
			});
		});

		// Exceptions requiring attention
		exceptions.slice(0, 3).forEach((exception) => {
			const conditionRef = exception.condition;
			const conditionId = typeof conditionRef === 'number' ? conditionRef : conditionRef?.id;
			const conditionName = typeof conditionRef === 'object' ? conditionRef?.name : null;
			const conditionFromList = conditionId
				? conditions.find((c) => c.id === conditionId)
				: undefined;
			items.push({
				id: `exc-${exception.id}`,
				type: 'condition',
				title: `Exception: ${conditionName || conditionFromList?.name || `Condition #${conditionId}`}`,
				subtitle: exception.reason || 'Condition override requires attention',
				severity: 'urgent',
				link: `/project/${projectId}/spend-plans/payment-condition/${conditionId}`,
			});
		});

		// Generated vouchers (not yet ready)
		const generatedVouchers = vouchers.filter((v) => v.status === 'GENERATED');
		generatedVouchers.slice(0, 3).forEach((voucher) => {
			items.push({
				id: `gen-voucher-${voucher.id}`,
				type: 'payment',
				title: `Voucher #${voucher.id} - Awaiting Verification`,
				subtitle: 'Payment voucher generated but not yet verified',
				severity: 'warning',
				link: `/project/${projectId}/spend-plans/voucher/${voucher.id}`,
				amount: voucher.amount,
				currency: voucher.currency || defaultCurrency,
			});
		});

		// Sort: urgent first, then warning, then info
		const severityOrder = { urgent: 0, warning: 1, info: 2 };
		items.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

		return items;
	}, [conditions, vouchers, exceptions, projectId, defaultCurrency]);

	// Activity status distribution for chart
	const activityStatusData = useMemo(() => {
		const distribution: Record<string, number> = {};
		activities.forEach((a) => {
			const status = a.status || 'UNKNOWN';
			distribution[status] = (distribution[status] || 0) + 1;
		});
		return Object.entries(distribution).map(([name, value]) => ({
			name: name.replace(/_/g, ' '),
			value,
		}));
	}, [activities]);

	// Condition status distribution for chart
	const conditionStatusData = useMemo(() => {
		const distribution: Record<string, number> = {};
		conditions.forEach((c) => {
			const status = c.status || 'UNKNOWN';
			distribution[status] = (distribution[status] || 0) + 1;
		});
		return Object.entries(distribution).map(([name, value]) => ({
			name: name.replace(/_/g, ' '),
			value,
		}));
	}, [conditions]);

	// Financial flow data (Planned → Committed → Paid)
	const financialFlowData = useMemo(() => {
		return [
			{
				name: 'Planned',
				amount: summary.totalAmount,
			},
			{
				name: 'Committed',
				amount: summary.totalCommitted,
			},
			{
				name: 'Paid',
				amount: summary.totalPaid,
			},
			{
				name: 'Remaining',
				amount: summary.totalRemaining,
			},
		];
	}, [summary]);

	// Group activities by work plan (used in Activity Tracker tab)
	const activitiesByWorkPlan = useMemo(() => {
		const grouped: Record<
			number,
			{ workPlan: (typeof workPlans)[0]; activities: typeof activities }
		> = {};

		activities.forEach((activity) => {
			const wpId =
				typeof activity.workPlan === 'number'
					? activity.workPlan
					: (activity.workPlan as { id: number })?.id || 0;
			if (!grouped[wpId]) {
				const wp = workPlans.find((w) => w.id === wpId);
				if (wp) {
					grouped[wpId] = { workPlan: wp, activities: [] };
				} else {
					grouped[wpId] = {
						workPlan: { id: wpId, name: 'Unassigned' } as (typeof workPlans)[0],
						activities: [],
					};
				}
			}
			grouped[wpId].activities.push(activity);
		});

		return Object.values(grouped);
	}, [activities, workPlans]);

	// Status colors for charts
	const STATUS_CHART_COLORS = [
		theme.palette.success.main,
		theme.palette.info.main,
		theme.palette.warning.main,
		theme.palette.error.main,
		theme.palette.grey[500],
	];

	// =========================================================================
	// Handlers
	// =========================================================================

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
	};

	const handleNavigate = (link: string) => {
		navigate(link);
	};

	// =========================================================================
	// Render: KPI Summary Cards
	// =========================================================================

	const renderKPICards = () => {
		const disbursementRate =
			summary.totalCommitted > 0
				? ((summary.totalPaid / summary.totalCommitted) * 100).toFixed(0)
				: '0';

		return (
			<Grid container spacing={2.5}>
				<Grid item xs={12} sm={6} md={3}>
					<SummaryCard
						label={t('spendPlan.opsDashboard.totalActivities')}
						value={summary.activityCount}
						icon={<IconActivity sx={{ color: theme.palette.primary.main }} />}
						color={theme.palette.primary.main}
						subtitle={`${activities.filter((a) => a.status === 'IN_PROGRESS').length} in progress`}
						onClick={() => navigate(`/project/${projectId}/spend-plans/activities`)}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<SummaryCard
						label={t('spendPlan.opsDashboard.totalContracts')}
						value={summary.contractCount}
						icon={<IconContract sx={{ color: theme.palette.info.main }} />}
						color={theme.palette.info.main}
						subtitle={`${formatCurrency({ value: summary.totalCommitted, currency: defaultCurrency })} committed`}
						onClick={() => navigate(`/project/${projectId}/spend-plans/contracts`)}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<SummaryCard
						label={t('spendPlan.opsDashboard.disbursementRate')}
						value={`${disbursementRate}%`}
						icon={<IconTrending sx={{ color: theme.palette.success.main }} />}
						color={theme.palette.success.main}
						progress={{
							value: Number(disbursementRate),
							label: `${formatCurrency({ value: summary.totalPaid, currency: defaultCurrency })} disbursed`,
						}}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<SummaryCard
						label={t('spendPlan.opsDashboard.pendingActions')}
						value={actionItems.length}
						icon={<IconPending sx={{ color: theme.palette.warning.main }} />}
						color={theme.palette.warning.main}
						subtitle={`${summary.conditionsPending} conditions, ${summary.vouchersReady} payments ready`}
					/>
				</Grid>
			</Grid>
		);
	};

	// =========================================================================
	// Render: Overview Tab
	// =========================================================================

	const renderOverviewTab = () => (
		<Box>
			{/* KPI Cards */}
			{renderKPICards()}

			<Grid container spacing={3} sx={{ mt: 0.5 }}>
				{/* Pending Actions */}
				<Grid item xs={12} lg={7}>
					<Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{ mb: 2.5 }}>
							<Stack direction="row" alignItems="center" spacing={1}>
								<IconAlert sx={{ color: theme.palette.warning.main, fontSize: 22 }} />
								<Typography variant="h6" fontWeight={600}>
									{t('spendPlan.opsDashboard.pendingActionsTitle')}
								</Typography>
								{actionItems.length > 0 && (
									<Chip
										label={actionItems.length}
										size="small"
										sx={{
											height: 22,
											fontWeight: 700,
											bgcolor: alpha(theme.palette.warning.main, 0.1),
											color: theme.palette.warning.main,
										}}
									/>
								)}
							</Stack>
						</Stack>

						{actionItems.length === 0 ? (
							<Box
								sx={{
									p: 4,
									textAlign: 'center',
									bgcolor: alpha(theme.palette.success.main, 0.04),
									borderRadius: 2,
								}}>
								<IconCheckCircle sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
								<Typography variant="subtitle1" fontWeight={600}>
									{t('spendPlan.opsDashboard.allCaughtUp')}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{t('spendPlan.opsDashboard.noPendingActions')}
								</Typography>
							</Box>
						) : (
							<Stack spacing={1.5}>
								{actionItems.slice(0, 8).map((item) => (
									<ActionItemCard key={item.id} item={item} onNavigate={handleNavigate} />
								))}
								{actionItems.length > 8 && (
									<Typography
										variant="body2"
										color="primary"
										sx={{ cursor: 'pointer', fontWeight: 500, textAlign: 'center', mt: 1 }}
										onClick={() => setTabValue(1)}>
										View all {actionItems.length} actions →
									</Typography>
								)}
							</Stack>
						)}
					</Paper>
				</Grid>

				{/* Charts sidebar */}
				<Grid item xs={12} lg={5}>
					<Stack spacing={3}>
						{/* Financial Flow */}
						<Paper sx={{ p: 3, borderRadius: 3 }}>
							<Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
								{t('spendPlan.opsDashboard.financialFlow')}
							</Typography>
							<ResponsiveContainer width="100%" height={200}>
								<BarChart data={financialFlowData} layout="vertical">
									<CartesianGrid
										strokeDasharray="3 3"
										stroke={alpha(theme.palette.divider, 0.5)}
										horizontal={false}
									/>
									<XAxis
										type="number"
										tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
										tickFormatter={(value) =>
											formatCurrency({ value, currency: defaultCurrency, notation: 'compact' })
										}
									/>
									<YAxis
										type="category"
										dataKey="name"
										tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
										width={80}
									/>
									<RechartsTooltip
										formatter={(value: number) =>
											formatCurrency({ value, currency: defaultCurrency })
										}
										contentStyle={{
											backgroundColor: theme.palette.background.paper,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: 8,
										}}
									/>
									<Bar dataKey="amount" radius={[0, 4, 4, 0]} maxBarSize={24}>
										{financialFlowData.map((entry, index) => (
											<Cell
												key={entry.name}
												fill={
													[
														theme.palette.primary.main,
														theme.palette.info.main,
														theme.palette.success.main,
														theme.palette.grey[400],
													][index]
												}
											/>
										))}
									</Bar>
								</BarChart>
							</ResponsiveContainer>
						</Paper>

						{/* Condition Completion */}
						<Paper sx={{ p: 3, borderRadius: 3 }}>
							<Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
								{t('spendPlan.opsDashboard.conditionStatus')}
							</Typography>
							{conditionStatusData.length > 0 ? (
								<ResponsiveContainer width="100%" height={180}>
									<PieChart>
										<Pie
											data={conditionStatusData}
											cx="50%"
											cy="50%"
											innerRadius={45}
											outerRadius={70}
											dataKey="value"
											paddingAngle={3}>
											{conditionStatusData.map((_entry, index) => (
												<Cell
													key={`cell-${index}`}
													fill={STATUS_CHART_COLORS[index % STATUS_CHART_COLORS.length]}
												/>
											))}
										</Pie>
										<RechartsTooltip
											contentStyle={{
												backgroundColor: theme.palette.background.paper,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 8,
											}}
										/>
										<Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
									</PieChart>
								</ResponsiveContainer>
							) : (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: 'center', py: 4 }}>
									No conditions data
								</Typography>
							)}
						</Paper>
					</Stack>
				</Grid>
			</Grid>
		</Box>
	);

	// =========================================================================
	// Render: Contract Pipeline Tab
	// =========================================================================

	const renderContractPipelineTab = () => {
		const sortedContracts = [...contractSummaries].sort((a, b) => {
			// Active first, then by pending actions
			if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
			if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') return 1;
			const aActions = a.invoicesPending + a.vouchersReady;
			const bActions = b.invoicesPending + b.vouchersReady;
			return bActions - aActions;
		});

		return (
			<Box>
				{/* KPI Cards */}
				{renderKPICards()}

				<Box sx={{ mt: 3 }}>
					<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
						<IconContract sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
						<Typography variant="h6" fontWeight={600}>
							{t('spendPlan.opsDashboard.contractPipeline')}
						</Typography>
						<Chip
							label={`${contracts.length} contracts`}
							size="small"
							sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.primary.main, 0.08) }}
						/>
					</Stack>

					{sortedContracts.length === 0 ? (
						<Paper
							sx={{
								p: 4,
								textAlign: 'center',
								borderRadius: 2,
								bgcolor: alpha(theme.palette.info.main, 0.04),
							}}>
							<IconContract sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
							<Typography variant="subtitle1" fontWeight={600}>
								No contracts yet
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Link contracts to activities to see the pipeline view
							</Typography>
						</Paper>
					) : (
						<Stack spacing={1.5}>
							{sortedContracts.map((row) => (
								<ContractRow key={row.contractId} row={row} projectId={projectId} />
							))}
						</Stack>
					)}
				</Box>
			</Box>
		);
	};

	// =========================================================================
	// Render: Activity Tracker Tab
	// =========================================================================

	const renderActivityTrackerTab = () => {
		return (
			<Box>
				{/* KPI Cards */}
				{renderKPICards()}

				<Grid container spacing={3} sx={{ mt: 0.5 }}>
					{/* Activity status chart */}
					<Grid item xs={12} md={5}>
						<Paper sx={{ p: 3, borderRadius: 3 }}>
							<Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
								{t('spendPlan.opsDashboard.activityStatus')}
							</Typography>
							{activityStatusData.length > 0 ? (
								<ResponsiveContainer width="100%" height={250}>
									<PieChart>
										<Pie
											data={activityStatusData}
											cx="50%"
											cy="50%"
											innerRadius={55}
											outerRadius={85}
											dataKey="value"
											paddingAngle={3}>
											{activityStatusData.map((_entry, index) => (
												<Cell
													key={`cell-${index}`}
													fill={STATUS_CHART_COLORS[index % STATUS_CHART_COLORS.length]}
												/>
											))}
										</Pie>
										<RechartsTooltip
											contentStyle={{
												backgroundColor: theme.palette.background.paper,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 8,
											}}
										/>
										<Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
									</PieChart>
								</ResponsiveContainer>
							) : (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: 'center', py: 4 }}>
									No activities data
								</Typography>
							)}
						</Paper>
					</Grid>

					{/* Activities grouped by work plan */}
					<Grid item xs={12} md={7}>
						<Paper sx={{ p: 3, borderRadius: 3 }}>
							<Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
								{t('spendPlan.opsDashboard.activitiesByWorkPlan')}
							</Typography>
							<Stack spacing={2}>
								{activitiesByWorkPlan.map(({ workPlan: wp, activities: wpActivities }) => {
									const completed = wpActivities.filter((a) => a.status === 'COMPLETED').length;
									const progress =
										wpActivities.length > 0 ? (completed / wpActivities.length) * 100 : 0;

									// Sum budgets
									const totalBudget = wpActivities.reduce((sum, a) => sum + (a.amount || 0), 0);
									const activityContracts = wpActivities.flatMap((a) =>
										getContractsByActivity(a.id)
									);
									const paid = activityContracts.reduce(
										(sum, c) => sum + (c.totalPaid || 0) + (c.totalPaidV1 || 0),
										0
									);

									return (
										<Paper
											key={wp.id}
											variant="outlined"
											sx={{
												p: 2,
												borderRadius: 2,
												cursor: 'pointer',
												'&:hover': {
													borderColor: theme.palette.primary.main,
												},
											}}
											onClick={() =>
												navigate(
													`/project/${projectId}/spend-plans/work-planning/work-plan/${wp.id}`
												)
											}>
											<Stack
												direction="row"
												alignItems="center"
												justifyContent="space-between"
												sx={{ mb: 1 }}>
												<Stack direction="row" alignItems="center" spacing={1}>
													<IconWorkPlan
														sx={{
															fontSize: 18,
															color: theme.palette.primary.main,
														}}
													/>
													<Typography variant="subtitle2" fontWeight={600}>
														{wp.name}
													</Typography>
												</Stack>
												<Typography variant="caption" color="text.secondary">
													{wpActivities.length} activities • {completed} completed
												</Typography>
											</Stack>
											<LinearProgress
												variant="determinate"
												value={progress}
												sx={{
													height: 4,
													borderRadius: 2,
													bgcolor: alpha(theme.palette.primary.main, 0.1),
													'& .MuiLinearProgress-bar': {
														borderRadius: 2,
														bgcolor: theme.palette.primary.main,
													},
												}}
											/>
											<Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
												<Typography variant="caption" color="text.secondary">
													Budget:{' '}
													{formatCurrency({
														value: totalBudget,
														currency: defaultCurrency,
													})}
												</Typography>
												<Typography variant="caption" color="success.main" fontWeight={600}>
													Paid:{' '}
													{formatCurrency({
														value: paid,
														currency: defaultCurrency,
													})}
												</Typography>
											</Stack>
										</Paper>
									);
								})}
								{activitiesByWorkPlan.length === 0 && (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ textAlign: 'center', py: 3 }}>
										No activities found
									</Typography>
								)}
							</Stack>
						</Paper>
					</Grid>
				</Grid>
			</Box>
		);
	};

	// =========================================================================
	// Main Render
	// =========================================================================

	return (
		<Box>
			{/* Header */}
			<HeaderListingPage
				icon={IconDashboard}
				title={t('spendPlan.opsDashboard.title')}
				subtitle={t('spendPlan.opsDashboard.subtitle')}
				variant="financial"
				stats={[
					{
						label: t('spendPlan.opsDashboard.totalPlanned'),
						value: formatCurrency({ value: summary.totalAmount, currency: defaultCurrency }),
					},
					{
						label: t('spendPlan.opsDashboard.totalCommitted'),
						value: formatCurrency({ value: summary.totalCommitted, currency: defaultCurrency }),
					},
					{
						label: t('spendPlan.opsDashboard.totalDisbursed'),
						value: formatCurrency({ value: summary.totalPaid, currency: defaultCurrency }),
						color: theme.palette.success.main,
					},
				]}
			/>

			{/* Tabs */}
			<Box sx={{ px: 3, pt: 2 }}>
				<Tabs
					value={tabValue}
					onChange={handleTabChange}
					aria-label="operations dashboard tabs"
					sx={{
						'& .MuiTab-root': {
							textTransform: 'none',
							fontWeight: 600,
							fontSize: '0.9rem',
							minHeight: 44,
						},
					}}>
					<Tab label={t('spendPlan.opsDashboard.tabs.overview')} />
					<Tab label={t('spendPlan.opsDashboard.tabs.contractPipeline')} />
					<Tab label={t('spendPlan.opsDashboard.tabs.activityTracker')} />
				</Tabs>
			</Box>

			{/* Tab Content */}
			<Box sx={{ px: 3 }}>
				<TabPanel value={tabValue} index={0}>
					{renderOverviewTab()}
				</TabPanel>
				<TabPanel value={tabValue} index={1}>
					{renderContractPipelineTab()}
				</TabPanel>
				<TabPanel value={tabValue} index={2}>
					{renderActivityTrackerTab()}
				</TabPanel>
			</Box>
		</Box>
	);
};
